import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Stethoscope, Upload, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DoctorsList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fileRef = useRef(null);
  const referralFileRef = useRef(null);
  const [doctors, setDoctors] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', specialization: '', commission_percentage: '' });

  const fetchDoctors = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/doctors`, { headers: { Authorization: `Bearer ${token}` } });
    setDoctors(await res.json());
  }, [token]);
  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const openCreate = () => { setEditDoc(null); setForm({ name: '', phone: '', email: '', specialization: '', commission_percentage: '' }); setShowDialog(true); };
  const openEdit = (d) => { setEditDoc(d); setForm({ name: d.name, phone: d.phone || '', email: d.email || '', specialization: d.specialization || '', commission_percentage: d.commission_percentage || '' }); setShowDialog(true); };
  const save = async () => {
    const url = editDoc ? `${API}/api/lis/doctors/${editDoc.id}` : `${API}/api/lis/doctors`;
    const res = await fetch(url, { method: editDoc ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success(editDoc ? 'Updated' : 'Created'); setShowDialog(false); fetchDoctors(); }
    else toast.error('Failed');
  };
  const deleteDoc = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/lis/doctors/${id}`, { method: 'DELETE', headers }); toast.success('Deleted'); fetchDoctors(); };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/lis/doctors/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json();
    toast.success(`Uploaded ${data.created} doctors`);
    if (data.errors?.length) toast.error(`${data.errors.length} errors`);
    fetchDoctors();
    e.target.value = '';
  };

  const handleReferralCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/lis/business/referrals/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json();
    toast.success(`Imported ${data.created} referral bills${data.doctors_created ? `, ${data.doctors_created} new doctor(s) auto-added` : ''}`);
    if (data.errors?.length) toast.error(`${data.errors.length} issues`, { description: data.errors.slice(0, 3).join('; ') });
    fetchDoctors();
    e.target.value = '';
  };

  return (
    <div data-testid="lis-doctors-list">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Referral Doctors</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/lis/business/templates/doctors`, '_blank')} data-testid="download-doctor-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <input type="file" ref={fileRef} accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="doctor-csv-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> CSV Upload
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/lis/business/templates/referrals`, '_blank')} data-testid="download-referral-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Referral Template
          </Button>
          <input type="file" ref={referralFileRef} accept=".csv" onChange={handleReferralCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => referralFileRef.current?.click()} data-testid="referral-csv-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import Referral Business
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="add-doctor-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Doctor</Button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {doctors.map(d => (
          <Card key={d.id} className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0"><Stethoscope className="w-4.5 h-4.5 text-teal-600" /></div>
                <div>
                  <p className="font-medium text-slate-800">{d.name}</p>
                  {d.specialization && <p className="text-xs text-slate-500">{d.specialization}</p>}
                  {d.phone && <p className="text-xs text-slate-400">{d.phone}</p>}
                  {d.commission_percentage > 0 && <p className="text-xs text-emerald-600 mt-1">Commission: {d.commission_percentage}%</p>}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(d)} className="p-1 text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteDoc(d.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </CardContent></Card>
        ))}
        {doctors.length === 0 && <Card className="border-dashed border-slate-300 col-span-full"><CardContent className="p-8 text-center text-slate-400"><Stethoscope className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No referral doctors yet.</p></CardContent></Card>}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}><DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{editDoc ? 'Edit Doctor' : 'Add Referral Doctor'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Doctor Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="doctor-name-input" />
          <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input placeholder="Specialization" value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} />
          <Input type="number" placeholder="Commission %" value={form.commission_percentage} onChange={e => setForm(f => ({ ...f, commission_percentage: e.target.value }))} />
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={save} data-testid="save-doctor-btn">{editDoc ? 'Update' : 'Create'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
